/**
 * The tab vocabulary and the URL rules of the Cost page (plan 2026-10-12-console-ward-billing,
 * D1, D11, D12, R-009).
 *
 * The Cost page is one rail item at `/platform/billing`. It holds four tabs, and the URL key `tab`
 * selects one. The four old paths `/platform/billing/<segment>` redirect to their tab and keep their
 * query. This module holds the rules that the host `BillingCostPane`, the four tab bodies, the
 * redirect in `AppHost` and the WS-4 tabs share. It is pure: no function here reads a clock, writes
 * the URL, changes an input or throws.
 */

/** The route of the Cost page. Every tab and every redirect lands on this path. */
export const COST_ROUTE = '/platform/billing';

/** The URL key that selects the tab. */
export const COST_TAB_PARAM = 'tab';

/** The four tabs, in display order. */
export const COST_TABS = ['ledger', 'gcp-bill', 'scale-events', 'rate-card'] as const;

/** One tab of the Cost page. */
export type CostTab = (typeof COST_TABS)[number];

/** The visible words of one tab. */
export interface CostTabMeta {
  /** The tab label, for example `GCP bill`. */
  readonly label: string;
  /** The question the tab answers. The host shows it as the page subtitle. */
  readonly question: string;
}

/**
 * The label and the question of each tab, from overview section 2. The Scale events entry holds no
 * word "cost": a scale event is a change of the run rate, never money spent (D10).
 */
export const COST_TAB_META: Readonly<Record<CostTab, CostTabMeta>> = {
  ledger: { label: 'Ledger', question: 'What did we spend, and on what?' },
  'gcp-bill': { label: 'GCP bill', question: "Does our estimate match Google's bill?" },
  'scale-events': { label: 'Scale events', question: 'What changed the run rate, and when?' },
  'rate-card': { label: 'Rate card', question: 'What price do we use per unit?' },
};

/**
 * The four URL keys that mean one thing on every tab that reads them: one window, one data plane,
 * one app (R-009 item 2). A write to one of them shows on every such tab. Every other key has one
 * owner tab: the Ledger owns `granularity`, `scope`, `orgId`, `resourceType` and `kind`, and GCP bill
 * owns `tier`.
 */
export const COST_SHARED_KEYS = ['startDate', 'endDate', 'dataPlaneId', 'appName'] as const;

/**
 * The tab that a raw `tab` value names.
 *
 * Input: the value of the `tab` key, or `null` when the URL has no such key. Output: the tab for an
 * exact match of one of {@link COST_TABS}; `ledger` for every other input, `null`, `''` and a case
 * variant such as `LEDGER` included (R-009 item 4).
 */
export function parseCostTab(raw: string | null): CostTab {
  const match = COST_TABS.find((tab) => tab === raw);
  return match ?? 'ledger';
}

/**
 * A copy of `current` with `tab` set to `tab`.
 *
 * Every other key keeps its values and its order. `tab` is the last key and holds one value: an old
 * `tab`, in any place and with any count, goes. `current` does not change.
 */
export function withCostTab(current: URLSearchParams, tab: CostTab): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete(COST_TAB_PARAM);
  next.append(COST_TAB_PARAM, tab);
  return next;
}

/**
 * The one write path of a tab into the URL (D11, R-009 item 2). WS-4 calls it too.
 *
 * Output: a new object. First, each key of `current` that `owned` does not name, with its values, in
 * order; `tab` and the keys of other tabs stay this way. Then, per key of `owned` in order, the first
 * value of `next` for that key when that value is a non-empty string. A key of `next` outside
 * `owned` is ignored. An owned key that is absent or `''` in `next` is absent from the output.
 * Neither input changes.
 */
export function mergeCostQuery(
  current: URLSearchParams,
  owned: readonly string[],
  next: URLSearchParams,
): URLSearchParams {
  const merged = new URLSearchParams();
  for (const [key, value] of current) {
    if (!owned.includes(key)) merged.append(key, value);
  }
  for (const key of owned) {
    const value = next.get(key);
    if (value !== null && value !== '') merged.set(key, value);
  }
  return merged;
}

/**
 * The link to one tab: `/platform/billing?` and the query of {@link withCostTab}.
 *
 * Input: the target tab, and the current query whose keys the link keeps. With no `current`, the
 * query holds `tab` alone.
 */
export function costTabHref(tab: CostTab, current?: URLSearchParams): string {
  return `${COST_ROUTE}?${withCostTab(current ?? new URLSearchParams(), tab).toString()}`;
}

/** The tab of each old path segment under `/platform/billing/` (D12). */
export const LEGACY_COST_SEGMENTS: Readonly<
  Record<'ledger' | 'reconciliation' | 'autoscale' | 'rate-card', CostTab>
> = {
  ledger: 'ledger',
  reconciliation: 'gcp-bill',
  autoscale: 'scale-events',
  'rate-card': 'rate-card',
};

/** One old path segment of the four that redirect. */
export type LegacyCostSegment = keyof typeof LEGACY_COST_SEGMENTS;

/**
 * The redirect target of one old path.
 *
 * Input: the old segment, and the raw `location.search` of the old URL, with or without its `?`.
 * Output: `pathname` is always {@link COST_ROUTE}, so no part of `search` reaches the path. `search`
 * is `?` plus {@link withCostTab} of the old query, with the tab from {@link LEGACY_COST_SEGMENTS}, so
 * an old `tab` value loses. `URLSearchParams` encodes every value, so `//host`, `../` and `%2F` stay
 * inside the query.
 */
export function legacyCostTarget(
  segment: LegacyCostSegment,
  search: string,
): { readonly pathname: string; readonly search: string } {
  return { pathname: COST_ROUTE, search: `?${withCostTab(new URLSearchParams(search), LEGACY_COST_SEGMENTS[segment]).toString()}` };
}

/**
 * The props of each tab body.
 *
 * `refreshKey` starts at 0 and grows by 1 per press of the host Refresh. A body puts it into the key
 * of each `useApiResource`, so a press reads every panel of the active tab again.
 */
export interface CostTabProps {
  readonly refreshKey: number;
}

/** One option of a filter select: the URL value and the text the trigger shows. */
export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

/**
 * The options of one list filter of a Cost tab (the Data plane and App selects), so an applied
 * filter always shows in its trigger.
 *
 * Input:
 * - `allLabel`: the text of the first option, value `''`, while the list loads or once it is read;
 * - `unavailableLabel`: the text of that option after the list read failed;
 * - `list`: `loading`, `error`, or `ready` with the options of the read;
 * - `applied`: the URL value of the filter, or `undefined`.
 *
 * Output: the `''` option, then the read options when `ready`, then one option `{ value: applied,
 * label: applied }` when `applied` is a non-empty value that no read option holds: the list still
 * loads, the read failed, or the value names a data plane or an app that is gone. A FilterSelect
 * whose value no option holds shows an empty trigger, and an empty trigger hides an active filter.
 * Pure; a new array on every call.
 */
export function listFilterOptions(args: {
  readonly allLabel: string;
  readonly unavailableLabel: string;
  readonly list: { readonly kind: 'loading' } | { readonly kind: 'error' } | { readonly kind: 'ready'; readonly options: readonly FilterOption[] };
  readonly applied: string | undefined;
}): readonly FilterOption[] {
  const { allLabel, unavailableLabel, list, applied } = args;
  const read = list.kind === 'ready' ? list.options : [];
  const options: FilterOption[] = [{ value: '', label: list.kind === 'error' ? unavailableLabel : allLabel }, ...read];
  if (applied !== undefined && applied !== '' && !read.some((option) => option.value === applied)) {
    options.push({ value: applied, label: applied });
  }
  return options;
}
